<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import CryptoJS from "crypto-js";
import { $t } from "@/locales";
import { useRouterPush } from "@/hooks/common/router";
import { useFormRules, useNaiveForm } from "@/hooks/common/form";
import { useCaptcha } from "@/hooks/business/captcha";
import { useAuthStore } from "@/store/modules/auth";
import { SPACE, NUMWORD, CHINESE } from "@/constants/reg";
defineOptions({
  name: "ResetPwd",
});

const { toggleLoginModule } = useRouterPush();
const { formRef, validate } = useNaiveForm();
const { label, isCounting, loading, getCaptcha } = useCaptcha();
const authStore = useAuthStore();

interface FormModel {
  email: string;
  code: string;
  password: string;
  confirmPassword: string;
}

let model: FormModel = reactive({
  email: "",
  code: "",
  password: "",
  confirmPassword: "",
});

type RuleRecord = Partial<Record<keyof FormModel, App.Global.FormRule[]>>;

const rules = computed<RuleRecord>(() => {
  const { formRules, createConfirmPwdRule } = useFormRules();

  return {
    email: formRules.email,
    password: formRules.pwd,
    code: formRules.code,
    confirmPassword: createConfirmPwdRule(model.password, "cPwd"),
  };
});

async function handleSubmit() {
  await validate();
  const backRes = await authStore.resetPassword(
    model.email,
    CryptoJS.SHA256(model.password).toString(),
    model.code,
  );
  if (backRes) {
    window.$message?.success?.($t("page.login.resetPwd.resetSuccess"));
    resetForm();

    setTimeout(() => {
      toggleLoginModule("pwd-login");
    }, 1500);
  }
}
function resetForm() {
  model.email = "";
  model.code = "";
  model.password = "";
  model.confirmPassword = "";
}

// 部分校验 只执行部分规则
const codeClickable = ref(true);
function validateEmail() {
  formRef.value?.validate(
    (errors) => {
      codeClickable.value = !!errors;
    },
    (rule) => {
      return rule?.key === "email";
    },
  );
}
function validateCode() {
  if (!emailToken.value) return;

  formRef.value?.validate(
    async (errors) => {
      if (!errors) {
        // 校验通过
        await authStore.getVerifyToken(model.code, model.email);
      }
    },
    (rule) => {
      return rule?.key === "code";
    },
  );
}

// 获取验证码
const emailToken = ref();
function getCode() {
  emailToken.value = getCaptcha(model.email);
}

const rightArr = reactive([false, false, false, false]);
function checkStatus(str: string) {
  rightArr[0] = str.length >= 6 && str.length <= 50;
  rightArr[1] = SPACE.test(str);
  rightArr[2] = NUMWORD.test(str);
  rightArr[3] = CHINESE.test(str);
}
</script>

<template>
  <NForm ref="formRef" :model="model" :rules="rules">
    <NFormItem
      :label="$t('form.email.label')"
      path="email"
      :show-require-mark="false"
    >
      <NInputGroup>
        <NInput
          v-model:value="model.email"
          :placeholder="$t('form.email.placeHolder')"
          @input="validateEmail"
          clearable
        >
          <template #prefix>
            <icon-local-mail-outline-rounded class="text-icon text-lightGrey" />
          </template>
        </NInput>
      </NInputGroup>
    </NFormItem>
    <NFormItem
      :label="$t('form.code.label')"
      path="code"
      :show-require-mark="false"
    >
      <div class="w-full flex-y-center gap-16px">
        <NInput
          v-model:value="model.code"
          :placeholder="$t('form.code.placeHolder')"
          @change="validateCode"
          clearable
        >
          <template #prefix>
            <svg-icon local-icon="captcha" class="text-icon" />
          </template>

          <template #suffix>
            <NButton
              type="primary"
              quaternary
              :disabled="isCounting || codeClickable"
              :loading="loading"
              @click="getCode"
            >
              {{ label }}
            </NButton>
          </template>
        </NInput>
      </div>
    </NFormItem>
    <NFormItem
      :label="$t('page.login.common.newPassword')"
      path="password"
      :show-require-mark="false"
    >
      <NPopover trigger="focus" :show-arrow="false" width="trigger">
        <template #trigger>
          <NInput
            v-model:value="model.password"
            type="password"
            show-password-on="click"
            :placeholder="$t('page.login.common.passwordPlaceholder')"
            :input-props="{ autocomplete: 'new-password' }"
            @input="checkStatus(model.password)"
          >
            <template #prefix>
              <svg-icon local-icon="password" class="text-icon" />
            </template>
          </NInput>
        </template>
        <!-- 6-50位字符，必须包含字母、数字 -->
        <div class="leading-7">
          <NSpace align="center" :class="{ 'text-success': rightArr[0] }">
            <icon-local-material-symbols-priority-outline-rounded
              class="text-20px"
            />
            <span>{{ $t("page.login.common.passStep1") }}</span>
          </NSpace>

          <NSpace align="center" :class="{ 'text-success': rightArr[1] }">
            <icon-local-material-symbols-priority-outline-rounded
              class="text-20px"
            />
            <span>{{ $t("page.login.common.passStep2") }}</span>
          </NSpace>
          <NSpace align="center" :class="{ 'text-success': rightArr[2] }">
            <icon-local-material-symbols-priority-outline-rounded
              class="text-20px"
            />
            <span>{{ $t("page.login.common.passStep3") }}</span>
          </NSpace>
          <NSpace align="center" :class="{ 'text-success': rightArr[3] }">
            <icon-local-material-symbols-priority-outline-rounded
              class="text-20px"
            />
            <span>{{ $t("page.login.common.passStep4") }}</span>
          </NSpace>
        </div>
      </NPopover>
    </NFormItem>
    <NFormItem
      :label="$t('form.confirmPwd.label')"
      path="confirmPassword"
      :show-require-mark="false"
    >
      <NInput
        v-model:value="model.confirmPassword"
        type="password"
        show-password-on="click"
        :placeholder="$t('page.login.common.confirmPasswordPlaceholder')"
      >
        <template #prefix>
          <svg-icon local-icon="password" class="text-icon" />
        </template>
      </NInput>
    </NFormItem>
    <NSpace vertical :size="18" class="w-full">
      <NButton
        type="primary"
        block
        :loading="authStore.loginLoading"
        @click="handleSubmit"
      >
        {{ $t("common.confirm") }}
      </NButton>
      <NButton block @click="toggleLoginModule('pwd-login')">
        {{ $t("page.login.common.back") }}
      </NButton>
    </NSpace>
  </NForm>
</template>

<style scoped></style>
